export const DECLINED_LIABILITY_PERCENT_DEFAULT = 100;
export const DECLINED_LIABILITY_PERCENT_MIN = 5;
export const DECLINED_LIABILITY_PERCENT_MAX = 100;
export const DECLINED_LIABILITY_PERCENT_STEP = 5;

/** Host-selectable values (high → low): 100, 95, 90, …, 5. */
export const DECLINED_LIABILITY_PERCENT_OPTIONS = Array.from(
  {
    length:
      (DECLINED_LIABILITY_PERCENT_MAX - DECLINED_LIABILITY_PERCENT_MIN) /
        DECLINED_LIABILITY_PERCENT_STEP +
      1,
  },
  (_, i) => DECLINED_LIABILITY_PERCENT_MAX - i * DECLINED_LIABILITY_PERCENT_STEP,
);

export function isAllowedDeclinedLiabilityPercent(value: number): boolean {
  return (
    value >= DECLINED_LIABILITY_PERCENT_MIN &&
    value <= DECLINED_LIABILITY_PERCENT_MAX &&
    value % DECLINED_LIABILITY_PERCENT_STEP === 0
  );
}

export function resolveDeclinedProtectionOutOfPocketPercent(options: {
  percent?: number | null;
  legacyPolicy?: string | null;
}): number {
  const { percent, legacyPolicy } = options;
  if (typeof percent === 'number' && percent >= 1 && percent <= DECLINED_LIABILITY_PERCENT_MAX) {
    return Math.trunc(percent);
  }
  if (legacyPolicy === 'UP_TO_THE_FULL_VALUE_OF_THE_VEHICLE') {
    return DECLINED_LIABILITY_PERCENT_DEFAULT;
  }
  return DECLINED_LIABILITY_PERCENT_DEFAULT;
}

/** Snaps stored or legacy values to the nearest allowed picker option. */
export function snapDeclinedLiabilityPercentToAllowedOption(percent: number): number {
  const resolved = resolveDeclinedProtectionOutOfPocketPercent({ percent });
  const snapped =
    Math.round(resolved / DECLINED_LIABILITY_PERCENT_STEP) * DECLINED_LIABILITY_PERCENT_STEP;
  return Math.min(
    DECLINED_LIABILITY_PERCENT_MAX,
    Math.max(DECLINED_LIABILITY_PERCENT_MIN, snapped),
  );
}

export function formatDeclinedLiabilityPercent(percent: number): string {
  const p = resolveDeclinedProtectionOutOfPocketPercent({ percent });
  if (p >= 100) {
    return 'Up to the full car price';
  }
  return `Up to ${p}% of the full car price`;
}

export function parseDeclinedLiabilityPercentInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(value) || !isAllowedDeclinedLiabilityPercent(value)) {
    return null;
  }
  return value;
}

export function formatProtectionOutOfPocketMax(options: {
  isDeclined: boolean;
  outOfPocket?: number | null;
  declinedProtectionOutOfPocketPercent?: number | null;
  declinedProtectionOutOfPocketAmt?: string | null;
}): string {
  const {
    isDeclined,
    outOfPocket,
    declinedProtectionOutOfPocketPercent,
    declinedProtectionOutOfPocketAmt,
  } = options;
  if (isDeclined) {
    return formatDeclinedLiabilityPercent(
      resolveDeclinedProtectionOutOfPocketPercent({
        percent: declinedProtectionOutOfPocketPercent,
        legacyPolicy: declinedProtectionOutOfPocketAmt,
      }),
    );
  }
  if (outOfPocket != null) {
    return `$${outOfPocket.toFixed(0)}`;
  }
  return '—';
}
